import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import { I18nLocalesEnum } from '@zaoblako/shared/common/enums/i18n.locales.enum';
import type { RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';
import { RoomFormDto, type RoomFormInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-form.dto';
import { RoomStatusEnum } from '@zaoblako/shared/modules/catalog/enums/room-status.enum';
import { RoomStatusEnumI18n } from '@zaoblako/shared/modules/catalog/i18n/room-status-enum.i18n';
import { RoomTypeEnumI18n } from '@zaoblako/shared/modules/catalog/i18n/room-type-enum.i18n';
import { RoomTypeEnum } from '@zaoblako/shared/modules/catalog/enums/room-type.enum';
import { RoomService } from '@zaoblako/web/core/services/catalog/room.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { ConverterMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/converter-multi-select/converter-multi-select.component';
import { NetPingDeviceMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/net-ping-device-multi-select/net-ping-device-multi-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { FileComponent } from '@zaoblako/web/shared/dumbs/file/file.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { FileUploaderComponent } from '@zaoblako/web/shared/components/file-uploader/file-uploader.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';

import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable } from 'rxjs';

/** Single status option for room status cards */
interface RoomStatusChoice {
  value: RoomStatusEnum;
  label: string;
}

@Component({
  selector: 'crm-room-form',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    ClientAutoCompleteComponent,
    ConverterMultiSelectComponent,
    NetPingDeviceMultiSelectComponent,
    DropdownModule,
    FileComponent,
    FileUploaderComponent,
    FilialSingleSelectComponent,
    FormsModule,
    InputNumberModule,
    InputTextComponent,
    JoinPipe,
    SpinnerComponent,
    UserMultiSelectComponent
  ],
  templateUrl: './room-form.component.html',
  styleUrls: ['./room-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoomFormComponent extends BaseFormComponent<RoomFormInterface> implements OnInit {
  @Input() public readonly index: number;

  public room = <RoomFormInterface>{};
  public dto = RoomFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public readonly roomTypeOptions = Object.entries(RoomTypeEnumI18n[I18nLocalesEnum.RU_RU]).map(([value, name]) => ({
    label: name,
    value: value as RoomTypeEnum
  }));

  private static readonly ROOM_STATUS_ORDER: RoomStatusEnum[] = [
    RoomStatusEnum.RENTED,
    RoomStatusEnum.FREE,
    RoomStatusEnum.RESERVE,
    RoomStatusEnum.ON_REPAIR
  ];

  public readonly roomStatusChoices: RoomStatusChoice[] = RoomFormComponent.ROOM_STATUS_ORDER.map((statusValue) => ({
    value: statusValue,
    label: RoomStatusEnumI18n[I18nLocalesEnum.RU_RU][statusValue]
  }));

  public readonly roomStatusEnum = RoomStatusEnum;

  public readonly maxRoomAttachmentBytes = 10 * 1024 * 1024;
  public readonly roomAttachmentAccept = '.pdf,.jpg,.jpeg,.png,.svg,image/jpeg,image/png,image/svg+xml,application/pdf';

  public constructor(
    private readonly roomService: RoomService,
    public readonly formStoreService: FormStoreService,
    public readonly globalService: GlobalService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.room.status = RoomStatusEnum.FREE;
    }
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);

    this.roomService.findById<RoomFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
      next: (room) => {
        this.room = room;
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      }
    });
  }

  public saveRoom(): void {
    if (this.checkValidate(this.room).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.roomService.update<RoomFormInterface, RoomFindInterface>(this.room).subscribe({
          next: (room) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Помещение "${this.room.name}" обновлено`);
            this.formStoreService.saveNext(room, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.roomService.create<RoomFindInterface>(this.room).subscribe({
          next: (room) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Помещение создано');
            this.formStoreService.saveNext(room, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteRoom(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить помещение "${this.room.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.roomService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Помещение "${this.room.name}" удалено`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public readonly onCancelClick = (): void => {
    this.formStoreService.closeSidebar(this.index);
  };

  public readonly selectRoomStatus = (status: RoomStatusEnum): void => {
    this.room.status = status;
    this.cdRef.markForCheck();
  };

  public readonly onRoomPlanFilesUploaded = (files: FileFindInterface[]): void => {
    const uploadedFile = files[0];
    if (uploadedFile) {
      this.room.roomPlan = uploadedFile;
      this.cdRef.markForCheck();
    }
  };

  public readonly onFloorPlanFilesUploaded = (files: FileFindInterface[]): void => {
    const uploadedFile = files[0];
    if (uploadedFile) {
      this.room.floorPlan = uploadedFile;
      this.cdRef.markForCheck();
    }
  };

  public readonly clearRoomPlan = (): void => {
    this.room.roomPlan = undefined;
    this.cdRef.markForCheck();
  };

  public readonly clearFloorPlan = (): void => {
    this.room.floorPlan = undefined;
    this.cdRef.markForCheck();
  };

  public readonly onUploadZoneClick = (event: MouseEvent, uploader: FileUploaderComponent): void => {
    if ((event.target as HTMLElement).closest('.room-form-upload__control')) {
      return;
    }
    uploader?.openFileDialog();
  };

  public readonly onUploadZoneKeydown = (event: KeyboardEvent, uploader: FileUploaderComponent): void => {
    if (event.code !== 'Enter' && event.code !== 'Space') {
      return;
    }
    event.preventDefault();
    uploader?.openFileDialog();
  };
}
